import React from "react";

import FormFieldXButton from "@v1/components/common/FormFieldXButton";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { MenuItem } from "@v1/components/designSystem/menu/MenuItem";
import {
  usePageFontOptions,
  useShopifyFontOptions,
} from "@v1/hooks/useFontFamilyOptions";
import { selectIsShopifyIntegrationEnabled } from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { useUploadedFontOptions } from "@v2/features/settings/shop/hooks/useUploadedFontOptions";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { filterOutPageFonts, GOOGLE_FONT_OPTIONS } from "@v2/utils/font";
import intersectionBy from "lodash-es/intersectionBy";
import { ChevronDown, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import ControlGroup from "../../extras/ControlGroup";
import { normalizeFontFamily } from "../../modifiers/utils";
import { fontStartCase } from "./utils";

interface FontFamilySelectorProps {
  fontFamily: string | null | undefined;
  onSelect: (fontValue: string | null) => void;
  placeholder: string;
  side?: "left" | "right";
  sideOffset?: number;
}

const FontFamilySelector: React.FC<FontFamilySelectorProps> = ({
  fontFamily,
  onSelect,
  placeholder,
  side = "right",
  sideOffset = 8,
}) => {
  const normalizedFontFamily = normalizeFontFamily(
    !isMixedStyleValue(fontFamily ?? null) ? fontFamily ?? null : null,
  )
    ?.replaceAll("_", " ")
    .replaceAll("-", " ");

  const { nameToDisplayName, fontOptions: shopifyFontOptions } =
    useShopifyFontOptions();

  const { fontOptions: uploadedFontOptions } = useUploadedFontOptions();
  const [fontPreviewErrors, setFontPreviewErrors] = React.useState<
    Record<string, boolean>
  >({});

  const pageFontOptions = usePageFontOptions();

  // NOTE (Gabe 2024-05-15): Only show the fonts that are actually available
  // (via Shopify or Google fonts).
  const filteredPageFonts = intersectionBy(
    pageFontOptions,
    [...shopifyFontOptions, ...uploadedFontOptions, ...GOOGLE_FONT_OPTIONS],
    (font) => font.value,
  );

  // Note (Evan, 2024-08-07): Attempt to retrieve a display name from
  // nameToDisplayName, checking first for the fontFamily, then the
  // fontFamily after removing any comma-joined fallbacks
  let displayName: string | undefined = undefined;
  if (fontFamily && !isMixedStyleValue(fontFamily)) {
    displayName =
      nameToDisplayName[fontFamily] ??
      nameToDisplayName[fontFamily.split(",")[0] ?? ""];
  }

  const fontDisplayName = displayName ?? normalizedFontFamily;

  const fontOptions = [
    // First get all page fonts
    ...filteredPageFonts.map((font) => {
      return createFontOption(
        font,
        nameToDisplayName,
        "From Page",
        fontDisplayName ?? "",
      );
    }),

    // Note (Evan, 6/2/23) Filter out page fonts from shopify/google fonts so that we don't have any duplicates
    ...filterOutPageFonts(
      [...shopifyFontOptions, ...uploadedFontOptions],
      filteredPageFonts,
    ).map((font) => {
      return createFontOption(
        font,
        nameToDisplayName,
        "From Theme",
        fontDisplayName ?? "",
      );
    }),
    ...filterOutPageFonts(GOOGLE_FONT_OPTIONS, filteredPageFonts).map(
      (font) => ({
        ...font,
        displayValue:
          getDisplayName(font.value, nameToDisplayName) ?? font.label,
        groupTitle: "Google Fonts",
        label: !fontPreviewErrors[font.value ?? ""] ? (
          <MenuItem
            variant="default"
            size="sm"
            selected={
              typeof fontFamily === "string"
                ? fontFamily?.includes(font.value ?? "")
                : false
            }
          >
            <img
              src={`/images/font-previews/${font.value?.replace(/\s+/g, "_")}.webp`}
              alt={getDisplayName(font.value, nameToDisplayName) ?? font.label}
              width={182}
              height={16}
              loading="lazy"
              onError={() => {
                return setFontPreviewErrors((prev) => ({
                  ...prev,
                  [font.value ?? ""]: true,
                }));
              }}
            />
          </MenuItem>
        ) : (
          font.label
        ),
      }),
    ),
  ];

  return (
    <ControlGroup label="Font">
      <div className="w-full overflow-hidden">
        <Combobox.Root
          options={fontOptions.filter((opt) => opt.value !== null)}
          value={
            fontFamily && !isMixedStyleValue(fontFamily) ? fontDisplayName : ""
          }
          onChange={onSelect}
        >
          <Combobox.Trigger>
            <Combobox.SelectionButton
              layoutClassName="w-full"
              title={fontDisplayName ?? placeholder}
              isPlaceholder={!fontDisplayName}
              size="sm"
              endEnhancer={
                fontFamily ? (
                  <div className="ml-auto">
                    <FormFieldXButton
                      onClick={() => {
                        onSelect("");
                      }}
                    />
                  </div>
                ) : (
                  <ChevronDown size={12} className="text-muted" />
                )
              }
            />
          </Combobox.Trigger>
          <Combobox.Popover
            sideOffset={sideOffset}
            side={side}
            align="center"
            layoutClassName="w-[220px] h-[416px]"
          >
            <Combobox.Content
              title="Fonts"
              areOptionsSearchable
              inputPlaceholder="Search fonts"
              emptySearchMessage="No matches found. Try uploading your own font!"
              footer={
                <Combobox.Footer size="sm">
                  <UploadCustomFont />
                </Combobox.Footer>
              }
            />
          </Combobox.Popover>
        </Combobox.Root>
      </div>
    </ControlGroup>
  );
};

const UploadCustomFont = () => {
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();
  const isShopifyIntegrationEnabled = useEditorSelector(
    selectIsShopifyIntegrationEnabled,
  );

  return (
    <>
      {isShopifyIntegrationEnabled ? (
        <div
          onClick={() => {
            navigate(`/shops/${projectId}/settings/shop/fonts`);
          }}
          className="flex cursor-pointer items-center border-none pt-0 text-primary font-medium"
        >
          <Plus size={18} className="mr-1" />
          <span className="text-xs">Upload Custom Fonts</span>
        </div>
      ) : (
        <DeprecatedButton
          variant="tertiary"
          style={{ minWidth: 0 }}
          disabled
          tooltipText="Connect to Shopify to add custom fonts"
        >
          Upload Custom Fonts
        </DeprecatedButton>
      )}
    </>
  );
};

const getDisplayName = (
  fontValue: string | null,
  nameToDisplayName: Record<string, string | undefined>,
) => {
  if (!fontValue) {
    return undefined;
  }
  return nameToDisplayName[fontValue];
};

const normalizeFontLabel = (
  label: string,
  displayName: string | undefined,
): string => {
  let normalizedLabel = label;

  if (displayName) {
    normalizedLabel = normalizeFontFamily(displayName) ?? label;
  } else {
    normalizedLabel = normalizeFontFamily(label) ?? label;
  }

  return fontStartCase(normalizedLabel);
};

const createFontOption = (
  font: { value: string | null; label: string },
  nameToDisplayName: Record<string, string | undefined>,
  groupTitle?: string,
  selectedFontFamily?: string | null,
) => {
  const displayName = getDisplayName(font.value, nameToDisplayName);
  const normalizedLabel = normalizeFontLabel(font.label, displayName);
  const fontLabel = (
    <MenuItem
      variant="default"
      size="sm"
      style={{
        fontFamily: `"${font.value}", "Inter", sans-serif`,
        fontSize: "13px",
        lineHeight: "20px",
      }}
      selected={selectedFontFamily === font.value}
    >
      {normalizedLabel}
    </MenuItem>
  );

  return {
    value: font.value ?? "",
    displayValue: normalizedLabel,
    label: fontLabel,
    groupTitle,
  };
};

export default FontFamilySelector;
